import React, { useState, useEffect } from "react";
import {
  Rocket, UserCheck, Clock, TrendingUp, X, Upload,
  Users, Building2, UserCog, Target, ChevronLeft,
  Activity, BarChart3, FileText, Settings
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  AreaChart, Area, ResponsiveContainer, CartesianGrid,
  XAxis, YAxis, Tooltip, Legend,
  BarChart, Bar, PieChart, Pie, Cell, RadialBarChart, RadialBar,
} from "recharts";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import StatCard from "../components/ui/StatCard";

const TOKEN = () => localStorage.getItem("token");
const apiFetch = (url) =>
  fetch(url, { headers: { Authorization: `Bearer ${TOKEN()}` } }).then((r) => r.json());

// ألوان موحدة مع المشروع
const PRIMARY = "#546E7A";
const DARK = "#263238";
const COLORS = ["#546E7A", "#78909C", "#90A4AE", "#B0BEC5", "#CFD8DC"];

const PIE_COLORS = ["#4CAF50", "#FF9800", "#F44336", "#2196F3", "#9C27B0"];

// ─── Quick Link Card ─────────────────────────────────────────────────────────
function QuickCard({ icon, title, value, sub, color, onClick }) {
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-4"
    >
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}18` }}
      >
        {React.cloneElement(icon, { size: 26, color })}
      </div>
      <div className="flex-1 text-right min-w-0">
        <p className="text-gray-400 text-xs mb-0.5">{sub}</p>
        <h4 className="text-2xl font-black text-[#263238]">{value ?? "—"}</h4>
        <p className="text-sm font-semibold text-[#546E7A] truncate">{title}</p>
      </div>
      <ChevronLeft size={16} className="text-gray-300 flex-shrink-0" />
    </div>
  );
}

// ─── Section Title ────────────────────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <div className="w-1 h-6 rounded-full bg-[#546E7A]" />
      <h3 className="font-bold text-[#263238] text-base">{children}</h3>
    </div>
  );
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-3 text-right text-sm font-ibm">
      <p className="text-gray-400 text-xs mb-2">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-bold">
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setSidebarOpen] = useState(window.innerWidth > 1024);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [user] = useState(() => {
    try { return JSON.parse(localStorage.getItem("user")); }
    catch { return null; }
  });
  const isAdmin = user?.roleId === 1;

  // ── Data States ──
  const [issueStats, setIssueStats] = useState({ total: 0, solved: 0, pending: 0, solveRate: 0 });
  const [chartData, setChartData] = useState([]);
  const [deptCount, setDeptCount] = useState(null);
  const [empCount, setEmpCount] = useState(null);
  const [userCount, setUserCount] = useState(null);
  const [activeUsers, setActiveUsers] = useState(null);
  const [planStats, setPlanStats] = useState({ planned: 0, actual: 0, pct: 0, activities: 0 });
  const [issuesByStatus, setIssuesByStatus] = useState([]);
  const [recentIssues, setRecentIssues] = useState([]);
  const [empByDept, setEmpByDept] = useState([]);

  useEffect(() => {
    document.title = "لوحة تحكم - الإدارة العامة للعمليات والتوقيع الإلكتروني";
    if (!TOKEN()) { navigate("/"); return; }
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      // Issues
      const issues = await apiFetch("/api/issues");
      if (Array.isArray(issues)) {
        const solved = issues.filter((i) => i.mechanismName === "تم الحل").length;
        const pending = issues.filter((i) => i.mechanismName !== "تم الحل").length;
        setIssueStats({
          total: issues.length, solved, pending,
          solveRate: issues.length > 0 ? Math.round((solved / issues.length) * 100) : 0,
        });

        // Chart - by date
        const dateMap = {};
        issues.forEach((item) => {
          const date = new Date(item.createdAt).toLocaleDateString("en-CA");
          if (!dateMap[date]) dateMap[date] = { date, solved: 0, pending: 0 };
          if (item.mechanismName === "تم الحل") dateMap[date].solved++;
          else dateMap[date].pending++;
        });
        setChartData(Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date)).slice(-14));

        // By status pie
        const statusMap = {};
        issues.forEach((i) => {
          const s = i.mechanismName || "غير محدد";
          statusMap[s] = (statusMap[s] || 0) + 1;
        });
        setIssuesByStatus(Object.entries(statusMap).map(([name, value]) => ({ name, value })));

        // Recent 5
        setRecentIssues([...issues].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5));
      }
    } catch {}

    try {
      // Departments
      const depts = await apiFetch("/api/departments");
      if (Array.isArray(depts)) setDeptCount(depts.length);
    } catch {}

    try {
      // Employees
      const emps = await apiFetch("/api/employees");
      if (Array.isArray(emps)) {
        setEmpCount(emps.length);
        // Group by dept
        const dMap = {};
        emps.forEach((e) => {
          const d = e.departmentName || "غير محدد";
          dMap[d] = (dMap[d] || 0) + 1;
        });
        setEmpByDept(Object.entries(dMap).map(([name, value]) => ({ name, value })).slice(0, 6));
      }
    } catch {}

    try {
      // Users
      const users = await apiFetch("/api/users");
      if (Array.isArray(users)) {
        setUserCount(users.length);
        setActiveUsers(users.filter((u) => u.status === "مفعل").length);
      }
    } catch {}

    try {
      // Plan
      const periods = await apiFetch("/api/plan/periods");
      if (Array.isArray(periods) && periods.length > 0) {
        const latest = periods[0];
        const acts = await apiFetch(`/api/plan/periods/${latest.id}/activities`);
        if (Array.isArray(acts)) {
          const planned = acts.reduce((s, a) => s + (Number(a.plannedVolume) || 0), 0);
          const actual = acts.reduce((s, a) => s + (Number(a.actualVolume) || 0), 0);
          setPlanStats({
            planned, actual, activities: acts.length,
            pct: planned > 0 ? Math.round((actual / planned) * 100) : 0,
          });
        }
      }
    } catch {}
  };

  if (!user) return <div className="min-h-screen flex items-center justify-center font-ibm">جاري التحميل...</div>;

  const pctColor = planStats.pct >= 100 ? "#4CAF50" : planStats.pct >= 75 ? "#FF9800" : "#F44336";

  return (
    <div className="flex min-h-screen bg-[#F4F6F8] font-ibm" dir="rtl">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setSidebarOpen(!isSidebarOpen)} />

      <div className={`flex-1 transition-all duration-300 p-4 md:p-8 ${isSidebarOpen ? "lg:mr-64" : "lg:mr-20"}`}>
        <Header isSidebarOpen={isSidebarOpen} setSidebarOpen={setSidebarOpen} title="لوحة التحكم" />

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#546E7A]">
              أهلاً بك مجدداً، {user?.firstName} {user?.lastName}
            </h2>
            <p className="text-gray-400 text-sm mt-1">نظرة شاملة على أداء النظام</p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-[#546E7A] hover:bg-[#263238] text-white px-8 py-3 rounded-2xl font-bold flex items-center gap-2 transition shadow-lg shadow-[#546e7a20] active:scale-95"
            >
              <span className="text-xl">+</span> مهمة جديدة
            </button>
          )}
        </div>

        {/* ── Stats Row ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard title="إجمالي المشاكل" value={issueStats.total}
            icon={<Rocket size={36} className="text-[#546E7A]" />}
            badge="كل البيانات" badgeColor="text-gray-500 bg-gray-50" />
          <StatCard title="تم الحل" value={issueStats.solved}
            icon={<UserCheck size={36} className="text-[#546E7A]" />}
            badge="مكتمل" badgeColor="text-green-500 bg-green-50" />
          <StatCard title="قيد المراجعة" value={issueStats.pending}
            icon={<Clock size={36} className="text-[#546E7A]" />}
            badge="معلق" badgeColor="text-orange-500 bg-orange-50" />
          <StatCard title="نسبة الحل" value={`${issueStats.solveRate}%`}
            icon={<TrendingUp size={36} className="text-[#546E7A]" />}
            badge="الأداء" badgeColor="text-purple-500 bg-purple-50" />
        </div>

        {/* ── Quick Links ── */}
        <div className="mb-8">
          <SectionTitle>نظرة سريعة على النظام</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <QuickCard icon={<Building2 />} title="الإدارات" value={deptCount}
              sub="إجمالي" color="#546E7A" onClick={() => navigate("/departments")} />
            <QuickCard icon={<Users />} title="الموظفين" value={empCount}
              sub="إجمالي" color="#1565C0" onClick={() => navigate("/employees")} />
            <QuickCard icon={<UserCog />} title="المستخدمين النشطين" value={`${activeUsers ?? "—"} / ${userCount ?? "—"}`}
              sub="مفعّل" color="#2E7D32" onClick={() => navigate("/users")} />
            <QuickCard icon={<Target />} title="أنشطة الخطة" value={planStats.activities}
              sub="الخطة الشهرية" color="#E65100" onClick={() => navigate("/planadmin")} />
          </div>
        </div>

        {/* ── Charts Row 1 ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">

          {/* Area Chart - حركة المشاكل */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <SectionTitle>حركة المشاكل (آخر 14 يوم)</SectionTitle>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="gSolved" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#4CAF50" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gPending" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF9800" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#FF9800" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area type="monotone" dataKey="solved" stroke="#4CAF50" strokeWidth={2}
                  fill="url(#gSolved)" name="تم الحل" />
                <Area type="monotone" dataKey="pending" stroke="#FF9800" strokeWidth={2}
                  fill="url(#gPending)" name="قيد المراجعة" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Pie - توزيع حسب الحالة */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <SectionTitle>توزيع المشاكل</SectionTitle>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={issuesByStatus} cx="50%" cy="50%" innerRadius={45} outerRadius={75}
                  dataKey="value" nameKey="name">
                  {issuesByStatus.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5 mt-2">
              {issuesByStatus.slice(0, 4).map((s, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">{s.value}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-600 font-medium">{s.name}</span>
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Charts Row 2 ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">

          {/* Bar - موظفين حسب إدارة */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <SectionTitle>توزيع الموظفين على الإدارات</SectionTitle>
            {empByDept.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={empByDept} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="الموظفين" radius={[0, 8, 8, 0]}>
                    {empByDept.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-gray-300 text-sm">لا توجد بيانات</div>
            )}
          </div>

          {/* Radial - نسبة إنجاز الخطة */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col">
            <SectionTitle>إنجاز الخطة الشهرية</SectionTitle>
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="relative">
                <ResponsiveContainer width={160} height={160}>
                  <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="90%"
                    startAngle={90} endAngle={-270}
                    data={[{ value: planStats.pct, fill: pctColor }]}>
                    <RadialBar dataKey="value" cornerRadius={8} background={{ fill: "#f0f0f0" }} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-black" style={{ color: pctColor }}>{planStats.pct}%</span>
                  <span className="text-xs text-gray-400">إنجاز</span>
                </div>
              </div>
              <div className="mt-4 w-full space-y-2 text-sm">
                <div className="flex justify-between text-gray-500">
                  <span className="font-bold text-[#263238]">{planStats.planned}</span>
                  <span>المخطط</span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span className="font-bold text-[#263238]">{planStats.actual}</span>
                  <span>المنجز</span>
                </div>
              </div>
              <button onClick={() => navigate("/planadmin")}
                className="mt-4 w-full py-2 rounded-xl bg-[#546E7A] text-white text-xs font-bold hover:bg-[#263238] transition">
                عرض الخطة
              </button>
            </div>
          </div>
        </div>

        {/* ── Recent Issues Table ── */}
      

      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[200] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-[#263238]/60 backdrop-blur-sm" onClick={() => setIsModalOpen(false)} />
            <div className="relative bg-white w-full max-w-xl rounded-[2.5rem] shadow-2xl overflow-hidden font-ibm text-right">
              <div className="p-8 border-b border-gray-50 flex justify-between items-center bg-[#F8F9FA]">
                <h3 className="text-2xl font-bold text-[#263238]">إنشاء مهمة جديدة</h3>
                <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-200 rounded-full">
                  <X size={24} className="text-gray-400" />
                </button>
              </div>
              <form className="p-8 space-y-6">
                <input type="text" placeholder="عنوان المهمة"
                  className="w-full p-4 rounded-2xl bg-[#F8F9FA] border border-gray-200" />
                <textarea rows="3" placeholder="الوصف"
                  className="w-full p-4 rounded-2xl bg-[#F8F9FA] border border-gray-200" />
                <div className="border-2 border-dashed border-gray-200 rounded-[2rem] p-6 text-center">
                  <Upload className="mx-auto text-gray-400 mb-2" />
                  رفع مرفقات
                </div>
                <div className="flex gap-4">
                  <button className="flex-1 bg-[#546E7A] text-white py-3 rounded-2xl font-bold">حفظ</button>
                  <button type="button" onClick={() => setIsModalOpen(false)}
                    className="flex-1 bg-gray-100 py-3 rounded-2xl">إلغاء</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;